import {
  CircularProgress,
  Grid,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  DataGridPro,
  GridActionsCellItem,
  GridCellEditStartReasons,
  GridRowEditStopReasons,
  GridRowModes,
} from '@mui/x-data-grid-pro';
import React, { useCallback, useEffect, useState } from 'react';

import {
  blockCompetencyGradesRowId,
  deleteCompetencyGradesRow,
  getCompetencyGrades,
  moveCompetencyGradesRowId,
  saveCompetencyGradesRow,
  saveCompetencyGradesRowId,
} from '../../../../../packages/common/api';
import BlockColoredIcon from '../../../../../packages/common/shared-ui/src/icons/BlockColoredIcon';
import ChevronIcon from '../../../../../packages/common/shared-ui/src/icons/ChevronIcon';
import DeleteColoredIcon from '../../../../../packages/common/shared-ui/src/icons/DeleteColoredIcon';
import DeleteIconBig from '../../../../../packages/common/shared-ui/src/icons/DeleteIconBig';
import EditColoredIcon from '../../../../../packages/common/shared-ui/src/icons/EditColoredIcon';
import PlusIcon from '../../../../../packages/common/shared-ui/src/icons/PlusIcon';
import ProjectCloseIcon from '../../../../../packages/common/shared-ui/src/icons/ProjectCloseIcon';
import ReorderIcon from '../../../../../packages/common/shared-ui/src/icons/ReorderIcon';
import SortIcon from '../../../../../packages/common/shared-ui/src/icons/SortIcon';
import SubmitIcon from '../../../../../packages/common/shared-ui/src/icons/SubmitIcon';
import UserNewEditIcon from '../../../../../packages/common/shared-ui/src/icons/UserNewEditIcon';
import { useTranslation } from '../../../../../packages/common/utils/getLocalString';

// Original class: fieldTextGrid
const StyledFieldTextGrid = styled(TextField)(theme => ({
  '& .MuiInputBase-root.MuiInput-root': {
    fontSize: '12px',
    lineHeight: '20px',
    '&::before': {
      borderBottom: 'none',
    },
    '&:hover': {
      '&::before': {
        borderBottom: 'none',
      },
    },
    '& .MuiInputBase-input.MuiInput-input': {
      color: '#7174AC',
      padding: '10px',
      '&::placeholder': {
        opacity: 1,
      },
    },
    '&.Mui-error': {
      border: '1px solid #E03737',
      '&::after': {
        display: 'none',
      },
    },
  },
}));
// Original class: DataGridTableRoot
const StyledDataGridTableRoot = styled(DataGridPro, {
  shouldForwardProp: prop => !['addRowsStyle', 'headlessStyle'].includes(prop),
})(({ theme, addRowsStyle, headlessStyle }) => ({
  width: '100%',
  overflow: 'hidden',
  '&.MuiDataGrid-root': {
    border: 0,
    ...(addRowsStyle && { borderRadius: '0' }),
    '& .MuiDataGrid-columnHeaders': {
      borderBottom: '1px solid #E4E4EF',
      ...(headlessStyle && { display: 'none' }),
      '& .MuiBadge-badge': {
        display: 'none',
      },
      '& .MuiDataGrid-columnHeader.MuiDataGrid-withBorderColor': {
        '&.tableGroupGanttHeaderCell': {},
        '&:not(.MuiDataGrid-columnHeaderCheckbox)': {
          '& + .MuiDataGrid-columnHeader.MuiDataGrid-withBorderColor': {
            borderLeft: '1px solid #E4E4EF',
            '&.actions': {
              borderLeft: 'none',
            },
          },
        },
      },
      '& .MuiDataGrid-columnHeader.MuiDataGrid-withBorderColor.MuiDataGrid-columnHeaderCheckbox':
        {
          minWidth: '36px!important',
          maxWidth: '36px!important',
          '& + .MuiDataGrid-columnHeader.MuiDataGrid-withBorderColor': {
            paddingLeft: 0,
          },
        },
    },
    '& .MuiDataGrid-virtualScroller': {
      '&::-webkit-scrollbar': {
        height: '6px',
        width: '6px',
      },
      '&::-webkit-scrollbar-thumb': {
        backgroundColor: '#7174AC',
        borderRadius: '5px',
      },
    },
    '& .MuiDataGrid-row': {
      width: '100%',
      borderBottom: '1px solid #E4E4EF',
      '& .MuiDataGrid-rowReorderCellContainer': {
        borderRight: '1px solid #E4E4EF',
      },
      '&.errorRow': {
        outline: '1px solid #E03737',
      },
      '& .MuiDataGrid-cell': {
        '&.tableCell': {
          overflow: 'visible',
          '&.parent': {
            paddingLeft: 0,
          },
          '&:not(.MuiDataGrid-columnHeaderCheckbox)': {
            '& + .MuiDataGrid-cell.tableCell': {
              borderLeft: '1px solid #E4E4EF',
            },
          },
          '&.tableGroupCell': {
            overflow: 'visible',
            whiteSpace: 'nowrap',
            '&:not(.MuiDataGrid-columnHeaderCheckbox)': {
              '& + .MuiDataGrid-cell.tableCell': {
                borderLeft: '1px solid transparent',
              },
            },
          },
          '&.tableGroupGanttCell': {
            padding: '10px 10px 10px 0',
            borderLeft: '1px solid #E4E4EF',
          },
        },
      },
      '& .MuiDataGrid-cell.MuiDataGrid-cellCheckbox': {
        minWidth: '36px!important',
        maxWidth: '36px!important',
        '& + .MuiDataGrid-cell': {
          paddingLeft: 0,
        },
      },
      '&.MuiDataGrid-row--editing': {
        boxShadow: 'none',
      },
    },
    '& .MuiDataGrid-row.groupRow': {
      '&.confirmationRow': {
        borderTop: '4px solid #E4E4EF',
        '&.first-row': {
          borderTop: 'none',
        },
      },
      '& .MuiDataGrid-cellCheckbox': {
        boxShadow: 'none',
        '& .MuiCheckbox-root': {
          visibility: 'hidden',
        },
      },
    },
    '& .MuiDataGrid-row .MuiDataGrid-cell:last-child': {
      borderRight: 0,
    },
    '& .MuiDataGrid-row.Mui-selected': {
      backgroundColor: '#DCDFF4',
    },
    '& .MuiDataGrid-row.Mui-selected.Mui-hovered': {
      backgroundColor: '#DCDFF4',
    },
    '& .MuiDataGrid-main .MuiDataGrid-iconButtonContainer .MuiButtonBase-root':
      {
        marginLeft: theme.spacing(2),
      },
    '& .MuiCheckbox-root': {
      '& .MuiSvgIcon-root': {
        width: '12px',
      },
    },
    '& .MuiDataGrid-columnSeparator': {
      '& .MuiSvgIcon-root': {
        width: '24px',
      },
    },
    '& .MuiDataGrid-cell': {
      borderBottom: 'none',
      '&.MuiDataGrid-cell--editing': {
        boxShadow: 'none',
        '&:focus-within': {
          outline: 'none',
        },
      },
      '&.tableCell': {
        wordBreak: 'break-word',
        borderColor: 'transparent',
        position: 'relative',
        padding: '10px',
        '&.tableSelectCell': {
          overflow: 'visible',
        },
        '& a': {
          position: 'relative',
        },
        '&:last-child': {
          boxShadow: 0,
        },
        '& .MuiCheckbox-root': {
          padding: 0,
        },
        '&.cellEdit': {
          padding: 0,
        },
        '&.selectCell': {
          padding: 0,
        },
      },
      '&.actions': {
        padding: 0,
        '& .MuiDataGrid-actionsCell': {
          height: '100%',
          width: '100%',
          gridGap: 0,
        },
        '& .MuiButtonBase-root.MuiIconButton-root': {
          height: '100%',
          borderRadius: '0',
          padding: '0',
          flex: 1,
          '& .MuiSvgIcon-root': {
            width: '16px',
            height: '16px',
          },
          '& + .MuiButtonBase-root.MuiIconButton-root': {
            borderLeft: '1px solid #E4E4EF',
          },
        },
      },
      '& a': {
        '&:hover': {
          textDecoration: 'underline',
        },
      },
    },
    '& .MuiDataGrid-cellCheckbox': {
      borderColor: 'transparent',
    },
    '& .MuiDataGrid-main': {
      backgroundColor: '#fff',
      ...(addRowsStyle
        ? { borderRadius: '16px 16px 0 0' }
        : headlessStyle
          ? { borderRadius: 0 }
          : { borderRadius: '16px' }),
    },
  },
  '& .MuiDataGrid-footerContainer': {
    marginTop: '20px',
    minHeight: 'auto',
    border: 'none',
    justifyContent: 'flex-start',
    '& .MuiToolbar-root.MuiTablePagination-toolbar': {
      minHeight: 'auto',
      padding: 0,
      '& .MuiPagination-root': {
        marginLeft: 0,
        '& li': {
          fontSize: '16px',
          lineHeight: '20px',
        },
        '& li + li': {
          marginLeft: '2px',
        },
        '& .MuiButtonBase-root.MuiPaginationItem-root': {
          color: '#41424E',
          fontSize: '16px',
          lineHeight: '20px',
          borderRadius: '4px',
          minWidth: '28px',
          height: '28px',
          margin: 0,
          '&.Mui-selected': {
            backgroundColor: '#6270D0',
            color: '#FAFAFC',
            '&:hover': {
              color: '#FAFAFC',
              backgroundColor: '#6270D0',
            },
          },
          '& .MuiSvgIcon-root.MuiPaginationItem-icon': {
            width: 'auto',
            height: 'auto',
          },
          '&:hover': {
            color: '#6270D0',
            backgroundColor: 'transparent',
          },
        },
        '& .MuiPaginationItem-ellipsis': {
          fontSize: '16px',
          lineHeight: '20px',
          color: '#41424E',
          padding: 0,
          margin: 0,
          minWidth: ' 28px',
        },
      },
    },
  },
}));

const CompetencyGrades = () => {
  const getLocalString = useTranslation();
  const [loading, setLoading] = useState(true);
  const [gridRows, setGridRows] = useState([]);
  const [gridColumns, setGridColumns] = useState([]);
  const [rowModesModel, setRowModesModel] = useState({});

  const handleDelete = useCallback(
    async (id, row) => {
      if (row.new) {
        const newArray = gridRows.filter(item => item.id !== row.id);
        setGridRows(newArray);
        return;
      }
      try {
        setLoading(true);
        const response = await deleteCompetencyGradesRow(id);
        if (response) {
          setGridRows(gridRows.filter(row => row.id !== id));
          setLoading(false);
        }
      } catch (error) {
        console.error('ERROR WITH DELETE', error);
      }
    },
    [gridRows],
  );

  const handleBlock = useCallback(
    async (id, row) => {
      try {
        setLoading(true);
        const response = await blockCompetencyGradesRowId(id);
        if (response) {
          const index = gridRows.findIndex(item => item.id === id);
          const updatedObject = {
            ...gridRows[index],
            isBlocked: !gridRows[index].isBlocked,
          };
          setGridRows([
            ...gridRows.slice(0, index),
            updatedObject,
            ...gridRows.slice(index + 1),
          ]);
          //setGridRows(gridRows.filter(row => row.id !== id));
          setLoading(false);
        }
      } catch (error) {
        console.error('ERROR WITH DELETE', error);
      }
    },
    [gridRows],
  );

  const handleMove = useCallback(
    async params => {
      try {
        setLoading(true);
        const previousRowId =
          params.targetIndex === 0
            ? null
            : gridRows[params.targetIndex - 1]?.id;
        const response = await moveCompetencyGradesRowId(params.row.id, {
          topGradeId: Number(previousRowId),
        });
        if (response) {
          const newRows = [...gridRows];
          const movedRow = newRows.splice(params.oldIndex, 1)[0];
          newRows.splice(params.targetIndex, 0, movedRow);
          setGridRows(newRows);
        }
      } catch (error) {
        console.error('ERROR WITH DELETE', error);
      } finally {
        setLoading(false);
      }
    },
    [gridRows],
  );

  const handleEditClick = useCallback(
    async id => {
      const index = gridRows.findIndex(item => item.id === id);
      const updatedObject = {
        id: id,
        ['grades.name']: gridRows[index]['grades.name'],
      };
      setGridRows([
        ...gridRows.slice(0, index),
        updatedObject,
        ...gridRows.slice(index + 1),
      ]);
      setRowModesModel(oldModel => ({
        ...oldModel,
        [id]: {
          mode: GridRowModes.Edit,
        },
      }));
    },
    [gridRows],
  );

  const handleField = useCallback(
    (data, id) => {
      let updatedObject;
      const pos = gridRows.findIndex(item => item.id === id);
      updatedObject = {
        ...gridRows[pos],
        ['grades.name']: data,
      };
      const newFields = [
        ...gridRows.slice(0, pos),
        updatedObject,
        ...gridRows.slice(pos + 1),
      ];
      setGridRows(newFields);
    },
    [gridRows],
  );

  const handleSaveClick = useCallback(
    async (id, row) => {
      const index = gridRows.findIndex(item => item.id === id);
      if (row['grades.name'] === '' || !row['grades.name']) {
        return;
      }
      let response;
      try {
        const data = {
          name: row['grades.name'],
        };
        if (rowModesModel[id].mode === 'edit' && !row.new) {
          response = await saveCompetencyGradesRowId(id, data);
        }
        if (row.new) {
          response = response = await saveCompetencyGradesRow(data);
        }
        if (response) {
          const { data: res } = response;
          if (row.new) {
            const currentRow = {
              ...res,
              ['grades.name']: res.name,
            };
            const pos = gridRows.findIndex(item => item.id === id);
            const newFields = [
              ...gridRows.slice(0, pos),
              currentRow,
              ...gridRows.slice(pos + 1),
            ];
            setGridRows(newFields);
          }
          setRowModesModel({
            ...rowModesModel,
            [id]: { mode: GridRowModes.View, ignoreModifications: true },
          });
        }
      } catch (error) {
        console.error('ERROR WITH GET', error);
      }
    },
    [gridRows, rowModesModel],
  );

  const formattedColumns = useCallback(() => {
    const smallFields = ['tracker'];
    const columns = gridColumns.map(column => {
      return {
        field: `${column.instance}.${column.fieldId}`,
        headerName: '',
        renderHeader: () => null,
        disableExport: false,
        position: column.position,
        flex: smallFields.includes(column.field) ? 0 : 1,
        width: smallFields.includes(column.field) && 250,
        cellClassName: props =>
          props.cellMode === 'view' ? 'tableCell' : 'tableCell cellEdit',
        editable: true,
        fieldType: column.fieldType,
        renderEditCell: props => {
          if (props.colDef.fieldType === 'string') {
            return (
              <StyledFieldTextGrid
                autoFocus
                placeholder={getLocalString('labels', 'input')}
                variant='standard'
                value={props.formattedValue ? props.formattedValue : ''}
                onChange={event =>
                  handleField(event.target.value, props.row.id)
                }
              ></StyledFieldTextGrid>
            );
          }
        },
      };
    });
    return [
      ...columns,
      {
        field: 'actions',
        type: 'actions',
        headerName: '',
        headerClassName: 'actions',
        width: 120,
        cellClassName: 'actions tableCell',
        getActions: ({ id, row }) => {
          const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit;
          let buttons = [
            <GridActionsCellItem
              onClick={() => handleDelete(id, row)}
              icon={
                <DeleteColoredIcon
                  width='16'
                  height='16'
                  viewBox='0 0 16 16'
                  color='#656985'
                />
              }
              label='Cancel'
            />,
          ];
          if (!row.new && !isInEditMode) {
            buttons.unshift(
              <GridActionsCellItem
                onClick={() => handleEditClick(id)}
                icon={
                  <EditColoredIcon
                    width='16'
                    height='16'
                    viewBox='0 0 16 16'
                    color='#656985'
                  />
                }
                label='Edit'
              />,
            );
            buttons.unshift(
              <GridActionsCellItem
                onClick={() => handleBlock(id, row)}
                icon={
                  <BlockColoredIcon
                    width='16'
                    height='16'
                    viewBox='0 0 16 16'
                    color={row.isBlocked ? '#656985' : '#DCDFF4'}
                  />
                }
                label='Block'
              />,
            );
          }
          if (isInEditMode) {
            buttons.unshift(
              <GridActionsCellItem
                onClick={() => handleSaveClick(id, row)}
                icon={<SubmitIcon />}
                label='Save'
              />,
            );
            if (!row.new) {
              buttons.unshift(
                <GridActionsCellItem
                  onClick={() => handleBlock(id, row)}
                  icon={
                    <BlockColoredIcon
                      width='16'
                      height='16'
                      viewBox='0 0 16 16'
                      color={row.isBlocked ? '#656985' : '#DCDFF4'}
                    />
                  }
                  label='Block'
                />,
              );
            }
          }

          return buttons;
        },
      },
    ];
  }, [
    gridColumns,
    getLocalString,
    handleField,
    rowModesModel,
    handleSaveClick,
    handleDelete,
    handleEditClick,
    handleBlock,
  ]);
  const columnsValue = formattedColumns();
  const formattedRows = useCallback(data => {
    return data.reduce((acc, row) => {
      const newCols = row.columns.reduce((columnAcc, _, i) => {
        return {
          ...columnAcc,
          [row.columns[i].fieldId]: row.columns[i].value,
        };
      }, {});
      return [
        ...acc,
        { ...newCols, id: row.rowId, isBlocked: row.services.isBlocked },
      ];
    }, []);
  }, []);

  const addRowHandler = useCallback(async () => {
    setGridRows(oldRows => [
      ...oldRows,
      {
        new: true,
        id: `row-${gridRows.length + 1}`,
        ['grades.name']: '',
      },
    ]);
    setRowModesModel(oldModel => ({
      ...oldModel,
      [`row-${gridRows.length + 1}`]: {
        mode: GridRowModes.Edit,
      },
    }));
  }, [gridRows.length]);

  const handleRowModesModelChange = useCallback(newRowModesModel => {
    setRowModesModel(newRowModesModel);
  }, []);

  const handleRowEditStop = useCallback((params, event) => {
    if (params.reason === GridRowEditStopReasons.rowFocusOut) {
      event.defaultMuiPrevented = true;
    }
  }, []);
  const processRowUpdate = useCallback(
    (newRow, oldRow) => {
      const updatedRow = { ...newRow, isNew: false };
      setGridRows(
        gridRows.map(row => (row.id === newRow.id ? updatedRow : row)),
      );
      return updatedRow;
    },
    [gridRows],
  );

  useEffect(() => {
    let ignore = false;
    async function getData() {
      const { data: userInfoValue } = await getCompetencyGrades();
      if (!ignore) {
        return userInfoValue;
      }
    }
    getData()
      .then(res => {
        setGridColumns(res.fields);
        setGridRows(formattedRows(res.rows));
        setLoading(false);
      })
      .catch(error => console.error(error, 'ERROR IN GET USER INFO'));
    return () => {
      ignore = true;
    };
  }, [formattedRows]);

  return (
    <Grid
      container
      direction={'column'}
      sx={{
        position: 'relative',
        width: '50%',
        marginTop: '20px',
        borderRadius: '16px',
        backgroundColor: '#fff',
      }}
    >
      {loading && (
        <Grid
          container
          sx={{
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            height: '100%',
            zIndex: 99,
            position: 'absolute',
            top: 0,
            left: 0,
          }}
        >
          <CircularProgress color='secondary' />
        </Grid>
      )}
      <Grid>
        <Grid
          sx={{
            padding: '16px 10px',
            borderBottom: '1px solid #E4E4EF',
          }}
        >
          <Typography
            variant='h3'
            sx={{
              p: 0,
              color: '#082253',
              fontWeight: '600',
            }}
          >
            {getLocalString('AdminCompetency', 'labels', 'scale')}
          </Typography>
        </Grid>
        <Grid>
          <StyledDataGridTableRoot
            headlessStyle={true}
            localeText={{
              noRowsLabel: getLocalString('labels', 'noData'),
            }}
            onRowEditStart={(params, event) => {
              if (params.reason === GridCellEditStartReasons.printableKeyDown) {
                event.defaultMuiPrevented = true;
              }
              if (params.reason === GridCellEditStartReasons.cellDoubleClick) {
                event.defaultMuiPrevented = true;
              }
            }}
            columnHeaderHeight={0}
            rowReordering
            autoHeight
            hideFooter
            disableRowSelectionOnClick
            disableColumnMenu
            columns={columnsValue}
            rows={gridRows}
            getRowClassName={params => !params.row.active && 'disabled'}
            slots={{
              rowReorderIcon: () => (
                <ReorderIcon width='16' height='16' viewBox='0 0 16 16' />
              ),
            }}
            getRowHeight={() => 'auto'}
            editMode='row'
            rowModesModel={rowModesModel}
            onRowModesModelChange={handleRowModesModelChange}
            onRowEditStop={handleRowEditStop}
            processRowUpdate={processRowUpdate}
            onRowOrderChange={(params, event) => handleMove(params)}
          />
          <Grid
            sx={{
              padding: '8px 16px',
              lineHeight: 'normal',
              borderRadius: '0 0 16px 16px',
              borderTop: gridRows.length > 0 ? 'none' : '1px solid #E4E4EF',
              backgroundColor: '#fff',
            }}
          >
            <IconButton
              onClick={addRowHandler}
              sx={{ padding: '0' }}
              disabled={gridRows.filter(item => item.new).length > 0}
            >
              <PlusIcon width='24' height='24' viewBox='0 0 24 24' />
            </IconButton>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default CompetencyGrades;
